// #region imports
import { AuthView } from 'app/area/auth';
import { MessagePanel } from 'app/components';
import { STATE } from 'app/constants';
import { enableNotifications } from 'app/infrastructure/notifications/index';
import { containerFactory } from 'app/infrastructure/redux';
import { layoutTranslation } from 'app/shell/constants';
import { DevErrorPanel } from '../components/dev-error-panel';
import { ModalHost } from 'app/shell/modal-host';
import { NotificationToast } from 'app/shell/notification-toast';
import * as React from 'react';
import * as shellActions from './actions';
import { AppShell } from '../app-shell';
import { Footer } from '../footer';
import { Header } from '../header';
import { MODAL } from '../modal-host-types';
import { LayoutProps, LayoutContainerProps } from '../types';
import { userIsLoggedIn, userInfoNotLoaded, getMenuAccess, shouldCheckExternalUserHandlingPolicy, externalUserHandlingPolicyIsAccepted } from '../selectors';
import { ApplicationState } from 'app/types-store';
import { logSystemInfo } from 'app/infrastructure/development/system-info';
import { MessagesToUserUnread } from 'app/shell/components/messages-to-user';
import { routeConstants } from 'app/routes/route-constants';
import { isRouteInPath } from 'app/area/shared/navigation';
import styles from '../layout.scss';
import { withRouter } from 'app/components/enhancers/with-router';
import { Outlet } from 'react-router-dom';
import { defaultScrollRestoration } from 'app/infrastructure/platform/routing';
//#endregion

/**
 * LayoutComponent performs the first level of application component rendering.
 * Wrapped by only react-router and context providers.
 *
 */
export class LayoutComponent extends React.Component<
  LayoutContainerProps
> {
  public static statePath: string = STATE.app;

  public componentDidUpdate(prev: LayoutProps) {
    defaultScrollRestoration();
    if (userInfoNotLoaded(this.props)) {
      this.props.loadUserInfo();
      shouldCheckExternalUserHandlingPolicy() && this.props.checkExternalUserHandlingPolicy(window.location.href.trim());
    }
    if (prev.userInfo !== this.props.userInfo) {
      logSystemInfo(this.props);
    }
  }

  public componentDidMount() {
    if (userInfoNotLoaded(this.props)) {
      this.props.loadUserInfo();
      shouldCheckExternalUserHandlingPolicy() && this.props.checkExternalUserHandlingPolicy(window.location.href.trim());
    }
    if (!this.props.locale) {
      this.props.changeUserLocale(AppShell.locale);
    }
    enableNotifications(null, (this.props as any).dispatch);
  }

  private onAboutModal = (e: any) => {
    e.preventDefault();
    this.props.displayModal(MODAL.about);
  };

  private onRefreshCache = (e: any) => {
    e.preventDefault();
    this.props.displayModal(MODAL.refreshCache);
  };

  private onRegenerateAggregatedEnv = (e: any) => {
    e.preventDefault();
    this.props.displayModal(MODAL.regenerateAggregatedEnv);
  };

  private onRegenerateAggregatedRisk = (e: any) => {
    e.preventDefault();
    this.props.displayModal(MODAL.regenerateAggregatedRisk);
  };

  public render() {
    const { messages, hideUserMessages, translation, print } = this.props;

    const body = userIsLoggedIn(this.props) && externalUserHandlingPolicyIsAccepted(this.props) && (
      <>
        <Header
          {...this.props}
          onRefreshCache={this.onRefreshCache}
          onRegenerateAggregatedEnv={this.onRegenerateAggregatedEnv}
          onRegenerateAggregatedRisk={this.onRegenerateAggregatedRisk}
          menuAccess={getMenuAccess(this.props.sharedState.access)} />
        <DevErrorPanel errors={this.props.errors} />
        <main>
          <MessagePanel messages={messages} />
          {!hideUserMessages && !print && <MessagesToUserUnread {...this.props} />}

          <NotificationToast
            location={this.props.router.location}
            userInfo={this.props.userInfo}
            consents={this.props.consents}
            sharedState={this.props.sharedState}
            fetchingLoadConsents={this.props.fetchingLoadConsents}
            loadConsents={this.props.loadConsents}
            showNotificationsToast={this.props.showNotificationsToast}
            translation={translation} />

          <Outlet />

          <ModalHost {...this.props as any} onClose={this.props.removeModal} />

        </main>
      </>
    );
    return (
      <div className={styles.layout}>
        <div className={styles.contentWrapper}>
          <AuthView {...this.props as any} />
          {body}
        </div>
        <footer>
          <Footer
            location={this.props.router.location}
            onAboutModal={this.onAboutModal} />
        </footer>
      </div>
    );
  }
}

const selector = {
  showBanner: (_: ApplicationState, props: LayoutProps) => props.router.location.pathname === '/',
  print: (_, props: LayoutProps) => (Object.keys(props.router.location.query) as any).includes('print'),
  hideUserMessages:
    (_: ApplicationState, { router: { location: { pathname }} }) => (
      isRouteInPath(pathname, routeConstants.messagesToUserView) ||
      isRouteInPath(pathname, routeConstants.administrationMessagesList) ||
      isRouteInPath(pathname, routeConstants.bowtie)
    )
};

const LayoutContainer = containerFactory<LayoutProps>(
  LayoutComponent,
  shellActions.actionCreators,
  selector,
  layoutTranslation
);

export const Layout = props => {
  const WrappedComponent = withRouter(LayoutContainer);
  return <WrappedComponent {...props} />; 
};
